import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BookOpen, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "../../analytics";
import type {
  PaginatedResponse,
  RegistryCategory,
  RegistryFilters,
  RegistrySeries,
} from "../../registry";
import { SeriesCard } from "./SeriesCard";
import { SeriesDetailSheet } from "./SeriesDetailSheet";

interface MageBookListProps {
  filters: RegistryFilters;
  page: number;
  onPageChange: (page: number) => void;
  categories?: RegistryCategory[];
  showCategories?: boolean;
  onCreateNew?: () => void;
  selectedSeriesId?: number;
  onSelectedSeriesIdChange?: (id: number | undefined) => void;
}

export function SeasonList({
  filters,
  page,
  onPageChange,
  categories = [],
  showCategories = false,
  onCreateNew,
  selectedSeriesId,
  onSelectedSeriesIdChange,
}: MageBookListProps) {
  const [selectedSeries, setSelectedSeries] = useState<RegistrySeries | null>(
    null,
  );

  const params = new URLSearchParams();
  if (filters.categoryId) params.set("category", filters.categoryId.toString());
  if (filters.genreId) params.set("genre", filters.genreId.toString());
  if (filters.query) params.set("q", filters.query);
  if (filters.sortBy) params.set("ordering", filters.sortBy);
  params.set("page", page.toString());

  const { data, isLoading, error, isFetching } = useQuery<
    PaginatedResponse<RegistrySeries>
  >({
    queryKey: ["registry", params.toString()],
    queryFn: () =>
      fetch(`/api/books/books/?${params.toString()}`).then((r) => r.json()),
    placeholderData: keepPreviousData,
  });

  // Sync selectedSeries with URL param and keep it fresh when data updates
  useEffect(() => {
    if (!data?.results) return;
    if (selectedSeriesId) {
      const fresh = data.results.find((b) => b.pk === selectedSeriesId);
      if (fresh) setSelectedSeries(fresh);
    } else {
      setSelectedSeries(null);
    }
  }, [data, selectedSeriesId]);

  const books = data?.results;
  const totalCount = data?.count ?? 0;
  const pageSize = 12;
  const totalPages = Math.ceil(totalCount / pageSize);

  if (isLoading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="overflow-hidden p-0">
            <Skeleton className="h-48 w-full rounded-none" />
            <CardContent className="p-4">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-destructive p-4">Error: {error.message}</div>;
  }

  if (!books || books.length === 0) {
    const hasFilters = !!(
      filters.query ||
      filters.categoryId ||
      filters.genreId
    );
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <div className="rounded-full bg-muted p-4">
          <BookOpen className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {hasFilters ? "No AudioGames found" : "No AudioGames yet"}
          </p>
          <p className="text-sm text-muted-foreground">
            {hasFilters
              ? "Try adjusting your filters."
              : "Create your first AudioGame to get started."}
          </p>
        </div>
        {onCreateNew && !hasFilters && (
          <Button size="sm" onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            New AudioGame
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* A list, so screen readers announce how many results there are and
          which one the user is on. */}
      <ul
        aria-label="AudioGames"
        className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4"
      >
        {books.map((book) => (
          <li key={book.pk}>
            <SeriesCard
              book={book}
              isSelected={selectedSeries?.pk === book.pk}
              onClick={() => {
                track("series_detail_opened", { series_id: book.pk });
                setSelectedSeries(book);
                onSelectedSeriesIdChange?.(book.pk);
              }}
            />
          </li>
        ))}
      </ul>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        disabled={isFetching}
        className="pt-2"
      />

      <SeriesDetailSheet
        selectedSeries={selectedSeries}
        onClose={() => {
          setSelectedSeries(null);
          onSelectedSeriesIdChange?.(undefined);
        }}
        categories={categories}
        showCategories={showCategories}
      />
    </>
  );
}
